import styled, { css, keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';
import { DEFAULT_LEVEL_COLOR, toRgba } from './getFlamePalette';

export const SelectedProfileRoot = styled.div`
	min-height: 92px;
	padding: 16px 14px 16px 18px;
	display: grid;
	grid-template-columns: 62px minmax(0, 1fr) auto;
	gap: 13px;
	align-items: center;
	background: linear-gradient(
		135deg,
		rgba(255, 255, 255, 0.075),
		rgba(255, 255, 255, 0.025)
	);
	border-bottom: 1px solid rgba(246, 142, 255, 0.14);
	backdrop-filter: blur(18px);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-height: 72px;
		padding: 10px ${THEME.mobileGutter};
		gap: 10px;

		${(props) =>
			props.$showBack
				? css`
						grid-template-columns: auto 48px minmax(0, 1fr) auto;
					`
				: css`
						grid-template-columns: 48px minmax(0, 1fr) auto;
					`}
	}
`;

export const ChatBackButton = styled.button`
	display: none;
	grid-column: 1;
	grid-row: 1;
	border: 0;
	border-radius: 10px;
	width: 36px;
	height: 36px;
	padding: 0;
	flex-shrink: 0;
	color: rgba(255, 245, 255, 0.92);
	background: rgba(255, 255, 255, 0.06);
	cursor: pointer;
	place-items: center;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		display: grid;
	}

	svg {
		width: 18px;
		height: 18px;
		fill: none;
		stroke: currentColor;
		stroke-width: 2.2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}
`;

export const ProfileActions = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	justify-self: end;
	min-width: 0;
	margin-left: auto;
`;

export const ProfileAvatarWrap = styled.div`
	position: relative;
	width: 62px;
	height: 62px;
	flex-shrink: 0;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 48px;
		height: 48px;
	}
`;

export const ProfileCallDot = styled.span`
	position: absolute;
	right: -1px;
	bottom: 3px;
	width: 15px;
	height: 15px;
	border-radius: 50%;
	border: 3px solid #150c1f;
	transition:
		background 180ms ease,
		box-shadow 180ms ease;

	${({ $waveMode }) => {
		if ($waveMode === 'user') {
			return css`
				background: #38bdf8;
				box-shadow: 0 0 10px rgba(56, 189, 248, 0.7);
			`;
		}

		if ($waveMode === 'character') {
			return css`
				background: #ff83e9;
				box-shadow: 0 0 10px rgba(255, 131, 233, 0.7);
			`;
		}

		return css`
			background: #6b7280;
			box-shadow: 0 0 8px rgba(107, 114, 128, 0.45);
		`;
	}}
`;

export const ProfileAvatar = styled.img`
	width: 62px;
	height: 62px;
	border-radius: 50%;
	object-fit: cover;
	border: 1px solid rgba(255, 255, 255, 0.28);
	box-shadow: 0 0 20px rgba(255, 77, 242, 0.16);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 48px;
		height: 48px;
	}
`;

export const ProfileMeta = styled.div`
	min-width: 0;
	display: flex;
	align-items: center;
	gap: 12px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		gap: 7px;
	}
`;

export const ProfileIdentity = styled.div`
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 5px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		gap: 3px;
	}
`;

export const ProfileName = styled.h3`
	min-width: 0;
	margin: 0;
	font-family: ${THEME.fontDisplay};
	font-size: 1.45rem;
	font-weight: 700;
	line-height: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: ${THEME.text};

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 1.1rem;
	}
`;

export const ProfileOrigin = styled.p`
	min-width: 0;
	margin: 0;
	flex: 0 1 auto;
	color: rgba(255, 255, 255, 0.68);
	font-size: 0.94rem;
	line-height: 1.25;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 0.8125rem;
	}
`;

export const ActiveCallPill = styled.div`
	justify-self: end;
	min-width: 148px;
	height: 44px;
	padding: 5px 6px 5px 13px;
	display: flex;
	align-items: center;
	gap: 10px;
	border-radius: 999px;
	backdrop-filter: blur(22px) saturate(130%);
	transition:
		border-color 180ms ease,
		background 180ms ease,
		box-shadow 180ms ease;

	${({ $waveMode }) => {
		if ($waveMode === 'user') {
			return css`
				border: 1px solid rgba(56, 189, 248, 0.38);
				background:
					radial-gradient(
						circle at 20% 0%,
						rgba(56, 189, 248, 0.2),
						transparent 48%
					),
					rgba(12, 34, 52, 0.62);
				box-shadow:
					inset 0 1px 0 rgba(255, 255, 255, 0.12),
					0 0 22px rgba(56, 189, 248, 0.2);
			`;
		}

		if ($waveMode === 'character') {
			return css`
				border: 1px solid rgba(255, 139, 239, 0.28);
				background:
					radial-gradient(
						circle at 20% 0%,
						rgba(255, 120, 237, 0.17),
						transparent 48%
					),
					rgba(44, 20, 50, 0.52);
				box-shadow:
					inset 0 1px 0 rgba(255, 255, 255, 0.12),
					0 0 22px rgba(221, 55, 255, 0.12);
			`;
		}

		return css`
			border: 1px solid rgba(255, 255, 255, 0.14);
			background: rgba(24, 24, 34, 0.52);
			box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
		`;
	}}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-width: 120px;
		height: 38px;
		padding: 4px 5px 4px 10px;
		gap: 8px;
	}
`;

export const CallButton = styled.button`
	position: relative;
	height: 42px;
	padding: 0 18px 0 12px;
	flex: 0 0 auto;
	display: inline-flex;
	align-items: center;
	gap: 10px;
	border: 1px solid rgba(255, 181, 249, 0.3);
	border-radius: 999px;
	color: #fff;
	background:
		radial-gradient(
			circle at 88% 18%,
			rgba(255, 190, 115, 0.28),
			transparent 42%
		),
		linear-gradient(135deg, rgba(176, 38, 255, 0.82), rgba(255, 77, 242, 0.62));
	box-shadow:
		inset 0 1px 0 rgba(255, 255, 255, 0.18),
		0 0 22px rgba(221, 55, 255, 0.2);
	backdrop-filter: blur(18px);
	cursor: pointer;
	transition:
		transform 180ms ease,
		border-color 180ms ease,
		box-shadow 180ms ease;

	&:hover,
	&:focus-visible {
		transform: translateY(-2px);
		border-color: rgba(255, 210, 251, 0.52);
		box-shadow:
			inset 0 1px 0 rgba(255, 255, 255, 0.22),
			0 0 30px rgba(230, 67, 255, 0.34);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		height: 36px;
		padding: 0 10px;
		gap: 0;
	}
`;

export const CallButtonIcon = styled.span`
	width: 28px;
	height: 28px;
	flex: 0 0 28px;
	display: grid;
	place-items: center;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.14);
	box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.16);

	svg {
		width: 15px;
		height: 15px;
		fill: none;
		stroke: currentColor;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 24px;
		height: 24px;
		flex-basis: 24px;

		svg {
			width: 13px;
			height: 13px;
		}
	}
`;

export const CallButtonLabel = styled.span`
	font-size: 0.84rem;
	font-weight: 800;
	letter-spacing: 0.01em;
	white-space: nowrap;
	color: rgba(255, 247, 255, 0.96);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		display: none;
	}
`;

export const ProfilePanelToggleArrow = styled.span`
	flex: 0 0 auto;
	width: 6px;
	height: 6px;
	border-top: 2px solid currentColor;
	border-right: 2px solid currentColor;
	transform: rotate(225deg);
	transition: transform 180ms ease;

	${(props) =>
		props.$panelOpen &&
		css`
			transform: rotate(45deg);
		`}
`;

export const ProfilePanelToggle = styled.button`
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 999px;
	width: 32px;
	min-width: 32px;
	height: 32px;
	flex: 0 0 32px;
	padding: 0;
	color: rgba(255, 245, 255, 0.88);
	background: linear-gradient(
		135deg,
		rgba(255, 255, 255, 0.11),
		rgba(255, 255, 255, 0.035)
	);
	box-shadow: 0 0 18px rgba(255, 77, 242, 0.08);
	backdrop-filter: blur(16px);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition:
		border-color 160ms ease,
		box-shadow 160ms ease,
		background 160ms ease,
		color 160ms ease,
		visibility 160ms ease;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 30px;
		min-width: 30px;
		height: 30px;
		flex-basis: 30px;
	}

	&:hover,
	&:focus-visible {
		color: #ffffff;
		border-color: rgba(255, 119, 244, 0.34);
		background: linear-gradient(
			135deg,
			rgba(255, 77, 242, 0.2),
			rgba(255, 255, 255, 0.06)
		);
		box-shadow: 0 0 22px rgba(255, 77, 242, 0.16);
	}

	${(props) =>
		props.$panelOpen &&
		css`
			visibility: hidden;
			pointer-events: none;
			width: 0;
			min-width: 0;
			flex-basis: 0;
			margin: 0;
			padding: 0;
			border-width: 0;
			overflow: hidden;
		`}
`;

export const CallWave = styled.span`
	height: 20px;
	display: inline-flex;
	align-items: center;
	gap: 2px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		height: 16px;
	}
`;

const wavePulse = keyframes`
	from {
		height: 5px;
		opacity: 0.62;
	}
	to {
		height: 19px;
		opacity: 1;
	}
`;

export const CallWaveBar = styled.i`
	width: 2px;
	height: 7px;
	border-radius: 999px;
	animation: ${wavePulse} 900ms ease-in-out infinite alternate;
	animation-play-state: ${({ $active }) => ($active ? 'running' : 'paused')};
	opacity: ${({ $active }) => ($active ? 1 : 0.35)};
	transition: opacity 180ms ease;

	${({ $waveMode }) => {
		if ($waveMode === 'user') {
			return css`
				background: linear-gradient(#38bdf8, #22d3ee);
				box-shadow: 0 0 8px rgba(56, 189, 248, 0.6);
			`;
		}

		if ($waveMode === 'character') {
			return css`
				background: linear-gradient(#ff83e9, #b855ff);
				box-shadow: 0 0 8px rgba(255, 80, 230, 0.6);
			`;
		}

		return css`
			background: rgba(255, 255, 255, 0.28);
			box-shadow: none;
		`;
	}}

	&:nth-child(2),
	&:nth-child(4) {
		animation-delay: -500ms;
	}

	&:nth-child(3) {
		animation-delay: -220ms;
	}
`;

export const CallTime = styled.time`
	min-width: 40px;
	color: rgba(255, 247, 255, 0.92);
	font-size: 0.78rem;
	font-variant-numeric: tabular-nums;
	font-weight: 750;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-width: 36px;
		font-size: 0.72rem;
	}
`;

export const HangupButton = styled.button`
	position: relative;
	width: 34px;
	height: 34px;
	flex: 0 0 34px;
	display: grid;
	place-items: center;
	border: 1px solid rgba(255, 149, 177, 0.42);
	border-radius: 50%;
	padding: 0;
	color: #fff;
	background: linear-gradient(
		145deg,
		rgba(255, 72, 130, 0.95),
		rgba(193, 24, 81, 0.88)
	);
	box-shadow: 0 0 17px rgba(255, 50, 111, 0.25);
	cursor: pointer;
	transition:
		transform 160ms ease,
		box-shadow 160ms ease;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 30px;
		height: 30px;
		flex-basis: 30px;

		svg {
			width: 16px;
			height: 16px;
		}
	}

	svg {
		width: 18px;
		height: 18px;
		fill: none;
		stroke: currentColor;
		stroke-width: 1.8;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	&:hover,
	&:focus-visible {
		transform: scale(1.06);
		box-shadow: 0 0 24px rgba(255, 50, 111, 0.42);
	}
`;

const flamePulse = keyframes`
	0%, 100% {
		filter:
			drop-shadow(0 0 2px var(--flame-glow-strong))
			drop-shadow(0 0 5px var(--flame-glow-soft));
	}
	50% {
		filter:
			drop-shadow(0 0 3px var(--flame-glow-strong))
			drop-shadow(0 0 8px var(--flame-glow-soft));
	}
`;

const flameLoadingPulse = keyframes`
	0%, 100% {
		opacity: 0.35;
	}
	50% {
		opacity: 0.85;
	}
`;

const levelUpPulse = keyframes`
	0%, 100% {
		filter:
			drop-shadow(0 0 6px var(--flame-glow-strong))
			drop-shadow(0 0 18px var(--flame-glow-mid))
			drop-shadow(0 0 32px var(--flame-glow-soft));
	}
	50% {
		filter:
			drop-shadow(0 0 10px var(--flame-glow-strong))
			drop-shadow(0 0 26px var(--flame-glow-mid))
			drop-shadow(0 0 42px var(--flame-glow-soft));
	}
`;

const modalIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(12px) scale(0.98);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
`;

export const FlameWrap = styled.span`
	position: relative;
	display: inline-grid;
	place-items: center;
`;

export const FlameSvg = styled.svg`
	--flame-glow-strong: ${({ $color }) =>
		toRgba($color || DEFAULT_LEVEL_COLOR, 0.9)};
	--flame-glow-mid: ${({ $color }) =>
		toRgba($color || DEFAULT_LEVEL_COLOR, 0.65)};
	--flame-glow-soft: ${({ $color }) =>
		toRgba($color || DEFAULT_LEVEL_COLOR, 0.4)};
	display: block;
	overflow: visible;
	transition:
		filter 0.35s ease,
		color 0.35s ease,
		opacity 0.35s ease;
	width: ${({ $size }) => {
		if ($size === 'hero') {
			return '72px';
		}

		if ($size === 'lg') {
			return '60px';
		}

		if ($size === 'sm') {
			return '26px';
		}

		return '28px';
	}};
	height: ${({ $size }) => {
		if ($size === 'hero') {
			return '92px';
		}

		if ($size === 'lg') {
			return '76px';
		}

		if ($size === 'sm') {
			return '32px';
		}

		return '35px';
	}};

	path {
		vector-effect: non-scaling-stroke;
	}

	&.flame--dim {
		color: #573064;
		opacity: 0.78;
	}

	&.flame--lit {
		filter:
			drop-shadow(0 0 2px var(--flame-glow-strong))
			drop-shadow(0 0 5px var(--flame-glow-soft));
		animation: ${flamePulse} 2.4s ease-in-out infinite;
	}

	&.flame--loading {
		animation: ${flameLoadingPulse} 1.4s ease-in-out infinite;
	}

	.flame__shine {
		filter: drop-shadow(
			0 0 2px ${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.7)}
		);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: ${({ $size }) => {
			if ($size === 'hero') {
				return '64px';
			}

			if ($size === 'lg') {
				return '44px';
			}

			if ($size === 'sm') {
				return '22px';
			}

			return '22px';
		}};
		height: ${({ $size }) => {
			if ($size === 'hero') {
				return '82px';
			}

			if ($size === 'lg') {
				return '58px';
			}

			if ($size === 'sm') {
				return '28px';
			}

			return '28px';
		}};
	}

	@media (prefers-reduced-motion: reduce) {
		&.flame--lit,
		&.flame--loading {
			animation: none;
		}
	}
`;

export const TemperaturePanel = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
	min-width: 0;
	margin: 0;
	padding: 6px 10px;
	border: 1px solid
		${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.45)};
	border-radius: 16px;
	background:
		linear-gradient(
			90deg,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.22)},
			rgba(20, 8, 30, 0.2) 40%,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.22)}
		),
		rgba(10, 4, 16, 0.92);
	box-shadow:
		0 0 0 1px ${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.08)}
			inset,
		0 10px 28px rgba(0, 0, 0, 0.28);
	color: inherit;
	cursor: pointer;
	transition:
		border-color 0.2s ease,
		transform 0.2s ease;

	&:hover,
	&:focus-visible {
		border-color: ${({ $color }) =>
			toRgba($color || DEFAULT_LEVEL_COLOR, 0.7)};
		transform: translateY(-1px);
	}

	&:disabled {
		cursor: default;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 4px 6px;
		border-radius: 13px;
	}
`;

export const TemperatureFlames = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;

	${FlameWrap}:nth-child(2) ${FlameSvg}.flame--lit {
		animation-delay: 0.35s;
	}

	${FlameWrap}:nth-child(3) ${FlameSvg}.flame--lit {
		animation-delay: 0.7s;
	}

	${FlameWrap}:nth-child(4) ${FlameSvg}.flame--lit {
		animation-delay: 1.05s;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		gap: 4px;
	}
`;

export const TemperatureBackdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1400;
	display: grid;
	place-items: center;
	padding: 16px;
	overflow-y: auto;
	background: rgba(2, 0, 7, 0.76);
	backdrop-filter: blur(10px);
`;

export const TemperatureClose = styled.button`
	position: absolute;
	top: 14px;
	right: 14px;
	z-index: 2;
	width: 40px;
	height: 40px;
	display: grid;
	place-items: center;
	border: 1px solid rgba(216, 189, 228, 0.28);
	border-radius: 50%;
	background: transparent;
	color: rgba(216, 189, 228, 0.85);
	font-size: 28px;
	font-weight: 200;
	line-height: 1;
	cursor: pointer;

	&:hover,
	&:focus-visible {
		color: #fff;
		border-color: ${({ $color }) =>
			toRgba($color || DEFAULT_LEVEL_COLOR, 0.65)};
		background: ${({ $color }) =>
			toRgba($color || DEFAULT_LEVEL_COLOR, 0.1)};
	}
`;

export const TemperatureDetailsModal = styled.section`
	position: relative;
	width: min(700px, 100%);
	max-height: calc(100dvh - 32px);
	padding: 26px 44px 28px;
	overflow-y: auto;
	border: 1px solid
		${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.68)};
	border-radius: 24px;
	background:
		radial-gradient(
			circle at 50% 0%,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.12)},
			transparent 35%
		),
		rgba(10, 4, 17, 0.98);
	box-shadow:
		0 0 12px ${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.28)},
		0 0 44px ${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.18)},
		0 30px 90px rgba(0, 0, 0, 0.8);
	color: ${THEME.text};
	animation: ${modalIn} 0.25s ease-out;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 24px 16px 20px;
	}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

export const TemperatureDetailsHeader = styled.header`
	padding: 0 40px;
	text-align: center;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 0 34px 0 0;
	}
`;

export const TemperatureDetailsTitle = styled.h2`
	margin: 0;
	font-size: clamp(1.35rem, 4vw, 1.8rem);
	font-weight: 500;
	color: ${THEME.text};
`;

export const TemperatureSubtitle = styled.p`
	margin: 3px 0 0;
	color: rgba(216, 189, 228, 0.62);
	font-size: 0.88rem;
	line-height: 1.4;
`;

export const TemperatureLevels = styled.div`
	display: grid;
	grid-template-columns: repeat(${({ $count }) => $count || 4}, 1fr);
	gap: 16px;
	margin: 18px 20px 10px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		gap: 6px;
		margin-inline: 0;
	}
`;

export const TemperatureLevelItem = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 5px;
	color: ${({ $active, $color }) =>
		$active ? $color || DEFAULT_LEVEL_COLOR : 'rgba(216, 189, 228, 0.7)'};
	font-size: 0.88rem;
	text-align: center;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 0.68rem;
	}
`;

export const TemperatureLevelName = styled.span`
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const TemperatureLevelHeading = styled.h3`
	margin: 4px 0 0;
	text-align: center;
	font-size: 1.35rem;
	font-weight: 500;
	color: ${({ $color }) => $color || DEFAULT_LEVEL_COLOR};
`;

export const TemperatureProgressTrack = styled.div`
	height: 24px;
	margin: 10px 38px 0;
	overflow: hidden;
	padding: 2px;
	border: 1px solid rgba(216, 189, 228, 0.2);
	border-radius: 999px;
	background: rgba(32, 17, 43, 0.72);

	span {
		display: block;
		height: 100%;
		border-radius: inherit;
		background: linear-gradient(
			90deg,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.85)},
			${({ $color }) => $color || DEFAULT_LEVEL_COLOR} 62%,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.9)}
		);
		box-shadow: 0 0 8px
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.8)};
		transition: width 0.45s ease;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		margin-inline: 5px;
	}
`;

export const TemperatureProgressLabel = styled.p`
	margin: 7px 0 0;
	color: rgba(216, 189, 228, 0.72);
	font-size: 0.88rem;
	text-align: center;
`;

export const TemperatureSep = styled.div`
	display: flex;
	align-items: center;
	gap: 14px;
	margin: 22px 0 14px;
	font-size: 1rem;
	text-align: center;
	color: ${({ $color }) => $color || DEFAULT_LEVEL_COLOR};

	&::before,
	&::after {
		content: '';
		height: 1px;
		flex: 1;
		background: linear-gradient(
			90deg,
			transparent,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.35)}
		);
	}

	&::after {
		background: linear-gradient(
			90deg,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.35)},
			transparent
		);
	}
`;

export const TemperatureFactors = styled.div`
	display: grid;
	gap: 9px;
	padding: 0 12px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 0;
	}
`;

export const TemperatureFactorRow = styled.div`
	display: grid;
	grid-template-columns: 28px 160px 34px 1fr;
	align-items: center;
	gap: 8px;
	font-size: 0.88rem;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		grid-template-columns: 24px minmax(90px, 1fr) 28px 1.5fr;
		gap: 5px;
		font-size: 0.75rem;
	}
`;

export const TemperatureFactorIcon = styled.span`
	font-size: 1.45rem;
	line-height: 1;
	color: ${({ $tone }) => {
		if ($tone === 'pink') {
			return '#ffd4e9';
		}

		if ($tone === 'magenta') {
			return '#ff725f';
		}

		if ($tone === 'violet') {
			return '#8ea0ff';
		}

		return '#ef73cd';
	}};
`;

export const TemperatureFactorLabel = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: rgba(255, 247, 255, 0.9);
`;

export const TemperatureFactorValue = styled.strong`
	color: #e9cfdd;
	font-weight: 500;
	font-variant-numeric: tabular-nums;
	text-align: right;
`;

export const TemperatureFactorBar = styled.div`
	height: 14px;
	overflow: hidden;
	border: 1px solid rgba(216, 189, 228, 0.18);
	border-radius: 999px;
	background: #190d23;
`;

export const TemperatureFactorFill = styled.span`
	display: block;
	height: 100%;
	border-radius: inherit;
	transition: width 0.45s ease;
	background: ${({ $tone }) => {
		if ($tone === 'pink') {
			return 'linear-gradient(90deg, #ff5c73, #ef42ba)';
		}

		if ($tone === 'magenta') {
			return 'linear-gradient(90deg, #e735bf, #ad42dc)';
		}

		if ($tone === 'violet') {
			return 'linear-gradient(90deg, #8347dc, #7054e9)';
		}

		return 'linear-gradient(90deg, #ff7656, #ec467d)';
	}};
	box-shadow: 0 0 8px
		${({ $tone }) => {
			if ($tone === 'pink') {
				return '#ef42ba';
			}

			if ($tone === 'magenta') {
				return '#ad42dc';
			}

			if ($tone === 'violet') {
				return '#7054e9';
			}

			return '#ec467d';
		}};
`;

export const LevelUpModal = styled.section`
	position: relative;
	width: min(420px, 100%);
	overflow: hidden;
	padding: 28px 28px 22px;
	border: 1px solid
		${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.72)};
	border-radius: 28px;
	text-align: center;
	background:
		radial-gradient(
			circle at 50% 12%,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.18)},
			transparent 42%
		),
		radial-gradient(
			circle at 80% 0%,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.14)},
			transparent 30%
		),
		#0b0412;
	box-shadow:
		0 0 18px ${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.3)},
		0 0 50px ${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.18)},
		0 28px 80px rgba(0, 0, 0, 0.75);
	color: ${THEME.text};
	animation: ${modalIn} 0.28s ease-out;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

export const LevelUpStars = styled.div`
	pointer-events: none;
	position: absolute;
	inset: 0;
	background-image:
		radial-gradient(
			circle at 18% 16%,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.85)} 0 1.2px,
			transparent 2px
		),
		radial-gradient(
			circle at 78% 12%,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.7)} 0 1px,
			transparent 2px
		),
		radial-gradient(
			circle at 62% 28%,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.55)} 0 1.4px,
			transparent 2.5px
		),
		radial-gradient(
			circle at 30% 34%,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.45)} 0 1px,
			transparent 2px
		),
		radial-gradient(
			circle at 86% 38%,
			${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.5)} 0 1px,
			transparent 2px
		);
`;

export const LevelUpHero = styled.div`
	--flame-glow-strong: ${({ $color }) =>
		toRgba($color || DEFAULT_LEVEL_COLOR, 1)};
	--flame-glow-mid: ${({ $color }) =>
		toRgba($color || DEFAULT_LEVEL_COLOR, 0.85)};
	--flame-glow-soft: ${({ $color }) =>
		toRgba($color || DEFAULT_LEVEL_COLOR, 0.45)};
	position: relative;
	display: grid;
	place-items: center;
	width: 120px;
	height: 120px;
	margin: 8px auto 6px;

	${FlameSvg} {
		position: relative;
		z-index: 1;
		color: ${({ $color }) => $color || DEFAULT_LEVEL_COLOR};
		animation: ${levelUpPulse} 2s ease-in-out infinite;
	}

	@media (prefers-reduced-motion: reduce) {
		${FlameSvg} {
			animation: none;
		}
	}
`;

export const LevelUpMinis = styled.div`
	display: flex;
	justify-content: center;
	gap: 12px;
	margin: 4px 0 18px;
`;

export const LevelUpTitle = styled.h2`
	margin: 0;
	font-size: clamp(1.6rem, 5vw, 2.1rem);
	font-weight: 600;
	letter-spacing: -0.02em;
`;

export const LevelUpEyebrow = styled.p`
	margin: 6px 0 0;
	color: rgba(216, 189, 228, 0.7);
	font-size: 0.94rem;
`;

export const LevelUpName = styled.p`
	margin: 4px 0 0;
	color: ${({ $color }) => $color || DEFAULT_LEVEL_COLOR};
	font-size: 1.5rem;
	font-weight: 600;
	text-shadow: 0 0 18px
		${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.55)};
`;

export const LevelUpBlurb = styled.p`
	max-width: 28ch;
	margin: 10px auto 0;
	color: rgba(255, 247, 255, 0.88);
	font-size: 0.88rem;
	line-height: 1.45;
`;

export const LevelUpCta = styled.button`
	width: 100%;
	margin-top: 22px;
	padding: 15px 20px;
	border: 0;
	border-radius: 999px;
	font-size: 1rem;
	font-weight: 600;
	color: #fff;
	background: linear-gradient(
		90deg,
		${({ $color }) => $color || DEFAULT_LEVEL_COLOR} 0%,
		${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.85)} 55%,
		${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.7)} 100%
	);
	box-shadow:
		0 0 18px ${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.45)},
		0 10px 28px ${({ $color }) => toRgba($color || DEFAULT_LEVEL_COLOR, 0.28)};
	cursor: pointer;

	&:hover,
	&:focus-visible {
		filter: brightness(1.06);
	}
`;

export const LevelUpFooter = styled.p`
	margin: 14px 0 0;
	color: rgba(216, 189, 228, 0.65);
	font-size: 0.82rem;
`;
